import React, { createContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { usePreferences } from '../../../../hooks/usePreferences';
import debounce from 'lodash/debounce';

// Date field names for special handling
const DATE_FIELDS = ['purchase_date', 'sale_date'];

export const TableContext = createContext(null);

const getInitialPreferences = (type = 'coin_list') => {
  try {
    const stored = localStorage.getItem('numisnavigator_preferences');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed[`${type}_preferences`] || null;
    }
  } catch (e) {
    console.error('Failed to parse stored preferences:', e);
  }
  return null;
};

export const TableProvider = ({ children, initialState = {}, type = 'coin_list', buyersPremium = 0, houseCurrency = 'USD', userCurrency = 'USD', profitMargin = 0, rate = 1, onFiltersClear }) => {
  const initialPrefs = getInitialPreferences(type);
  const { preferences, updatePreferences, error } = usePreferences(type);
  const [loading, setLoading] = useState(initialState.loading ?? false);

  // Keep loading in sync with parent
  useEffect(() => {
    setLoading(initialState.loading);
  }, [initialState.loading]);
  
  // Initialize state with stored preferences
  const [sortConfig, setSortConfig] = useState(() => {
    const savedConfig = initialPrefs?.sortConfig;
    return Array.isArray(savedConfig) ? savedConfig : [];
  });
  const [filterText, setFilterText] = useState(initialPrefs?.quickFilter || '');
  const [filters, setFilters] = useState(initialPrefs?.filters || []);
  const [columnSearchText, setColumnSearchText] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() => {
    if (initialPrefs?.columnWidths) return initialPrefs.columnWidths;
    if (preferences?.columnWidths) return preferences.columnWidths;
    return {};
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    if (initialPrefs?.columnOrder) return initialPrefs.columnOrder;
    if (preferences?.columnOrder) return preferences.columnOrder;
    return [];
  });

  const [currentColumnOrder, setCurrentColumnOrder] = useState(columnOrder);
  const [pinnedColumns, setPinnedColumns] = useState(() => {
    if (initialPrefs?.pinnedColumns) return initialPrefs.pinnedColumns;
    if (preferences?.pinnedColumns) return preferences.pinnedColumns;
    return [];
  });
  const [density, setDensity] = useState(() => {
    if (initialPrefs?.density) return initialPrefs.density;
    if (preferences?.density) return preferences.density;
    try {
      const stored = localStorage.getItem('numisnavigator_preferences');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.coin_list_preferences?.density) {
          return parsed.coin_list_preferences.density;
        }
      }
    } catch (e) {
      console.error('Failed to parse stored density preference:', e);
    }
    return 'standard';
  });

  // Initialize state from preferences once loading is complete
  useEffect(() => {
    if (!loading && !initialized && preferences) {
      setSortConfig(Array.isArray(preferences.sortConfig) ? preferences.sortConfig : []);
      setFilterText(preferences.quickFilter || '');
      setFilters(preferences.filters || []);
      if (preferences.density) {
        setDensity(preferences.density);
      }
      if (preferences.columnWidths) {
        setColumnWidths(preferences.columnWidths);
      }
      if (preferences.columnOrder) {
        setColumnOrder(preferences.columnOrder);
      }
      if (Array.isArray(preferences.pinnedColumns)) {
        setPinnedColumns(preferences.pinnedColumns);
      }
      setInitialized(true);
    }
  }, [loading, preferences, initialized]);

  // Keep preferences in sync
  useEffect(() => {
    if (preferences) {
      // Sync density
      if (preferences.density && preferences.density !== density) {
        setDensity(preferences.density);
      }
      
      // Sync pinned columns
      if (Array.isArray(preferences.pinnedColumns) && 
          JSON.stringify(preferences.pinnedColumns) !== JSON.stringify(pinnedColumns)) {
        setPinnedColumns(preferences.pinnedColumns);
      }
    }
  }, [preferences?.density, preferences?.pinnedColumns, density, pinnedColumns]);

  const setFilterTextAndSave = useCallback((value) => {
    setFilterText(value);
    updatePreferences(currentPrefs => ({
      ...currentPrefs,
      quickFilter: value
    }));
  }, [setFilterText, updatePreferences]);

  // Update preferences when sortConfig or filters change
  useEffect(() => {
    // Always save preferences to persist both additions and removals
    updatePreferences(currentPrefs => ({
      ...currentPrefs,
      sortConfig,
      filters
    }));
  }, [sortConfig, filters, updatePreferences]);

  const handleClearSort = useCallback(() => {
    setSortConfig([]);
  }, []);

  const handleSort = useCallback((column) => {
    setSortConfig(prevConfig => {
      const existingIndex = prevConfig.findIndex(c => c.column === column);
      if (existingIndex !== -1) {
        const existing = prevConfig[existingIndex];
        if (existing.order === 'ASC') {
          // Change to DESC
          return [
            ...prevConfig.slice(0, existingIndex),
            { column, order: 'DESC' },
            ...prevConfig.slice(existingIndex + 1)
          ];
        } else {
          // Remove this sort
          return [
            ...prevConfig.slice(0, existingIndex),
            ...prevConfig.slice(existingIndex + 1)
          ];
        }
      }
      // Add new sort
      return [...prevConfig, { column, order: 'ASC' }];
    });
  }, []);

  const handleFilterAdd = useCallback((filterOrFilters) => {
    if (Array.isArray(filterOrFilters)) {
      // For multiple filters, batch the update
      const newFilters = filterOrFilters.map((filter, index) => ({
        ...filter,
        // Only add OR operator if not the last filter
        logicOperator: index < filterOrFilters.length - 1 ? 'OR' : null
      }));
      setFilters(prevFilters => [...prevFilters, ...newFilters]);
    } else {
      // Single filter
      setFilters(prevFilters => [...prevFilters, filterOrFilters]);
    }
  }, []);

  const handleFilterChange = useCallback((index, newFilter) => {
    setFilters(prevFilters => {
      const newFilters = [...prevFilters];
      newFilters[index] = newFilter;
      return newFilters;
    });
  }, []);

  const handleFilterValueChange = useCallback((index, value) => {
    setFilters(prevFilters => {
      const newFilters = [...prevFilters];
      newFilters[index] = { ...newFilters[index], value };
      return newFilters;
    });
  }, []);

  const handleFilterRemove = useCallback((index) => {
    setFilters(prevFilters => prevFilters.filter((_, i) => i !== index));
  }, []);

  const handleFilterClear = useCallback((onClose) => {
    setFilters([]);
    updatePreferences(currentPrefs => ({
      ...currentPrefs,
      filters: []
    }));
    onFiltersClear?.();
    onClose?.();
  }, [updatePreferences, onFiltersClear]);

  const handleDensityChange = useCallback((newDensity) => {
    setDensity(newDensity);
    updatePreferences(currentPrefs => ({
      ...currentPrefs,
      density: newDensity
    }));
  }, [updatePreferences]);

  const toggleColumnVisibility = useCallback((columnField, forceShow = false) => {
    const hiddenColumns = new Set(preferences?.hiddenColumns || []);
    const currentWidths = { ...columnWidths }; // Preserve current widths
    
    if (Array.isArray(columnField)) {
      // Handle array of columns
      columnField.forEach(field => {
        if (forceShow) {
          hiddenColumns.delete(field);
        } else {
          const wasHidden = hiddenColumns.has(field);
          if (wasHidden) {
            hiddenColumns.delete(field);
          } else {
            hiddenColumns.add(field);
          }
        }
        // Ensure width is preserved
        if (!currentWidths[field]) {
          currentWidths[field] = 150; // Default width if none exists
        }
      });
    } else {
      // Handle single column
      const wasHidden = hiddenColumns.has(columnField);
      if (forceShow) {
        hiddenColumns.delete(columnField);
      } else if (wasHidden) {
        hiddenColumns.delete(columnField);
      } else {
        hiddenColumns.add(columnField);
      }
      // Ensure width is preserved
      if (!currentWidths[columnField]) {
        currentWidths[columnField] = 150; // Default width if none exists
      }
    }

    const newHiddenColumns = Array.from(hiddenColumns);
    
    // Update both hidden columns and preserve widths in one update
    updatePreferences(currentPrefs => ({
      ...currentPrefs,
      hiddenColumns: newHiddenColumns,
      columnWidths: currentWidths
    }));

    // Explicitly update column widths state to prevent recalculation
    setColumnWidths(currentWidths);
  }, [preferences?.hiddenColumns, updatePreferences]);

  const isColumnVisible = useCallback((columnField) => {
    return !(preferences?.hiddenColumns || []).includes(columnField);
  }, [preferences?.hiddenColumns]);

  const isColumnPinned = useCallback((columnField) => {
    return pinnedColumns.includes(columnField);
  }, [pinnedColumns]);

  // Update currentColumnOrder when columnOrder changes
  useEffect(() => {
    setCurrentColumnOrder(columnOrder);
  }, [columnOrder]);

  const getVisibleColumns = useCallback((columns) => {
    // Filter visible columns and sort by column order
    const orderMap = new Map(currentColumnOrder.map((field, index) => [field, index]));
    
    return columns
      .filter(column => isColumnVisible(column.field))
      .sort((a, b) => {
        const aIndex = orderMap.has(a.field) ? orderMap.get(a.field) : Number.MAX_SAFE_INTEGER;
        const bIndex = orderMap.has(b.field) ? orderMap.get(b.field) : Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });
  }, [isColumnVisible, currentColumnOrder, isColumnPinned]);

  const handleColumnReorder = useCallback((newOrder) => {
    setCurrentColumnOrder(newOrder);
    setColumnOrder(newOrder);
    updatePreferences(currentPrefs => ({
      ...currentPrefs,
      columnOrder: newOrder
    }));
  }, [updatePreferences]);

  const getDefaultOperator = useCallback(() => 'contains', []);

  const filterRows = useCallback((rows) => {
    if (!filterText && filters.length === 0) return rows;

    return rows.filter(row => {
      // Quick filter (global search)
      if (filterText) {
        // Get all direct row values and normalize them
        const rowValues = Object.values(row).map(value =>
          String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        );

        // Add tag names to searchable values if tags exist
        if (row.tags && Array.isArray(row.tags)) {
          row.tags.forEach(tag => {
            if (tag.name) {
              rowValues.push(tag.name.toLowerCase());
            }
          });
        }

        // Add custom field values to searchable values if they exist
        if (row.custom_fields && typeof row.custom_fields === 'object') {
          Object.values(row.custom_fields).forEach(value => {
            if (value !== null && value !== undefined) {
              rowValues.push(String(value).toLowerCase());
            }
          });
        }

        // Check if search contains commas
        if (filterText.includes(',')) {
          // Split by commas and use OR logic
          const searchTerms = filterText.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().split(',').map(term => term.trim()).filter(Boolean);
          const matchesQuickFilter = searchTerms.some(term =>
            rowValues.some(value => value.includes(term))
          );
          if (!matchesQuickFilter) return false;
        } else {
          // Split by spaces and use AND logic
          const searchTerms = filterText.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().split(/\s+/).map(term => term.trim()).filter(Boolean);
          const matchesQuickFilter = searchTerms.every(term =>
            rowValues.some(value => value.includes(term))
          );
          if (!matchesQuickFilter) return false;
        }
      }

      // Column filters
      if (filters.length > 0) {
        // Group filters by their logical relationship
        let currentGroup = [];
        const filterGroups = [];

        filters.forEach((filter, index) => {
          // Start a new group if:
          // 1. This is the first filter
          // 2. Previous filter didn't have an OR operator
          if (index === 0 || filters[index - 1].logicOperator !== 'OR') {
            if (currentGroup.length > 0) {
              filterGroups.push([...currentGroup]);
            }
            currentGroup = [filter];
          } else {
            // Add to current group if previous filter had OR operator
            currentGroup.push(filter);
          }
        });

        // Add the last group
        if (currentGroup.length > 0) {
          filterGroups.push(currentGroup);
        }

        // Each group is connected by AND, within groups connected by OR
        return filterGroups.every(group => {
          // Within each group, any filter can match (OR logic)
          return group.some(filter => {
            const rawValue = row[filter.column];
            const filterValue = filter.value.toLowerCase();

            if (filter.column === 'tags') {
              const tags = Array.isArray(row.tags) ? row.tags.map(t => t.name.toLowerCase()) : [];
              switch (filter.operator) {
                case 'contains':
                  return tags.some(t => t.includes(filterValue));
                case 'equals':
                  return tags.includes(filterValue);
                case 'isNot':
                  return !tags.includes(filterValue);
                default:
                  return true;
              }
            }
            
            // Handle isEmpty and isNotEmpty before string conversion
            if (filter.operator === 'isEmpty') {
              return rawValue === '' || rawValue === null || rawValue === undefined;
            }
            if (filter.operator === 'isNotEmpty') {
              return rawValue !== '' && rawValue !== null && rawValue !== undefined;
            }

            // Special handling for date fields
            if (DATE_FIELDS.includes(filter.column)) {
              // Compare dates as strings in YYYY-MM-DD format
              const dateValue = rawValue ? rawValue.split('T')[0] : '';
              
              switch (filter.operator) {
                case 'equals':
                  return dateValue === filter.value;
                case 'lessThanOrEqual':
                  return dateValue <= filter.value;
                case 'greaterThanOrEqual':
                  return dateValue >= filter.value;
                case 'between':
                  if (filter.value.includes(' to ')) {
                    const [start, end] = filter.value.split(' to ');
                    return dateValue >= start && dateValue <= end;
                  }
                  return false;
                default:
                  return false;
              }
            } else {
              // Regular string comparison for non-date fields
              const value = String(rawValue || '').toLowerCase();
              switch (filter.operator) {
                case 'contains':
                  return value.includes(filterValue);
                case 'equals':
                  return value === filterValue;
                case 'isNot':
                  return value !== filterValue;
                case 'startsWith':
                  return value.startsWith(filterValue);
                case 'endsWith':
                  return value.endsWith(filterValue);
                case 'containsAny':
                  const searchTerms = filterValue.split(',').map(term => term.trim().toLowerCase());
                  return searchTerms.some(term => value.includes(term));
                default:
                  return true;
              }
            }
          });
        });
      }

      return true;
    });
  }, [filterText, filters]);

  const sortRows = useCallback((rows) => {
    if (sortConfig.length === 0) return rows;

    return [...rows].sort((a, b) => {
      for (const { column, order } of sortConfig) {
        const aValue = a[column];
        const bValue = b[column];

        if (aValue === bValue) continue;

        let comparison;
        // Find column definition to check for custom sortComparator
        const columnDef = columns.find(col => col.field === column);
        if (columnDef?.sortComparator) {
          // Use column's custom sortComparator if available
          comparison = columnDef.sortComparator(aValue, bValue);
        } else if (column === 'lot_number') {
          // Special handling for lot numbers - convert to numbers for comparison
          const numA = parseFloat(aValue) || -Infinity;
          const numB = parseFloat(bValue) || -Infinity;
          comparison = numA < numB ? -1 : 1;
        } else if (column === 'user_coin_id') {
          // Special handling for user_coin_id column (format: YYYY-N)
          // Compare year first, then sequence number
          const partsA = String(aValue || '').split('-');
          const partsB = String(bValue || '').split('-');
          const yearA = parseInt(partsA[0], 10) || 0;
          const yearB = parseInt(partsB[0], 10) || 0;
          
          if (yearA !== yearB) {
            comparison = yearA < yearB ? -1 : 1;
          } else {
            // Same year, compare sequence numbers
            const seqA = parseInt(partsA[1], 10) || 0;
            const seqB = parseInt(partsB[1], 10) || 0;
            comparison = seqA < seqB ? -1 : seqA > seqB ? 1 : 0;
          }
        } else {
          // Default comparison - case insensitive for strings
          const strA = String(aValue || '').toLowerCase();
          const strB = String(bValue || '').toLowerCase();
          comparison = strA < strB ? -1 : 1;
        }
        return order === 'ASC' ? comparison : -comparison;
      }
      return 0;
    });
  }, [sortConfig]);

  const applyFilters = useCallback((rows) => {
    return filterRows(rows);
  }, [filterRows]);

  const handleColumnResize = useCallback((columnField, width) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnField]: Math.max(width, 50) // Minimum width of 50px
    }));
    updatePreferences(currentPrefs => ({
      ...currentPrefs,
      columnWidths: {
        ...(currentPrefs?.columnWidths || {}),
        [columnField]: Math.max(width, 50)
      }
    }));
  }, [updatePreferences]);

  const handleColumnAutoResize = useCallback((columnField, tableRef) => {
    // Find all cells in this column
    const dataCells = tableRef.current.querySelectorAll(`td[data-field="${columnField}"]`);
    const headerCell = tableRef.current.querySelector(`th[data-field="${columnField}"]`);
    
    // Measure data cells
    let maxDataWidth = 0;
    dataCells.forEach(cell => {
      const contentDiv = cell.querySelector('div');
      if (contentDiv) {
        // Clone the content div without its padding to get true content width
        const clone = contentDiv.cloneNode(true);
        clone.style.cssText = `
          position: absolute;
          visibility: hidden;
          padding: 0;
          white-space: nowrap;
        `;
        document.body.appendChild(clone);
        const contentWidth = clone.getBoundingClientRect().width;
        document.body.removeChild(clone);
        maxDataWidth = Math.max(maxDataWidth, contentWidth);
      }
    });

    // Add minimal padding (4px on each side)
    const finalDataWidth = maxDataWidth + 8;

    // Only measure header if it exists
    let headerWidth = 0;
    if (headerCell) {
      const headerText = headerCell.querySelector('.overflow-hidden');
      if (headerText) {
        // Clone and measure header text without any styles that might affect width
        const clone = headerText.cloneNode(true);
        clone.style.cssText = `
          position: absolute;
          visibility: hidden;
          padding: 0;
          margin: 0;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          white-space: nowrap;
        `;
        document.body.appendChild(clone);
        headerWidth = clone.getBoundingClientRect().width;
        document.body.removeChild(clone);
      }
    }

    // Calculate header width with minimal padding
    const pinIconWidth = isColumnPinned(columnField) ? 12 : 0; // Reduced from 14
    const sortIconWidth = 8; // Reduced from 12
    const headerPadding = 8; // 4px on each side
    const finalHeaderWidth = headerWidth + headerPadding + pinIconWidth + sortIconWidth;

    // Use the larger of data width or header width
    let finalWidth = Math.max(finalDataWidth, finalHeaderWidth);

    // Enforce minimum width
    finalWidth = Math.max(finalWidth, 50);

    // Check if this is the last visible column
    const visibleColumns = Array.from(tableRef.current.querySelectorAll('th[data-field]'))
      .filter(th => !th.style.display || th.style.display !== 'none')
      .map(th => th.getAttribute('data-field'));
    const isLastColumn = visibleColumns[visibleColumns.length - 1] === columnField;

    // For the last column, also consider remaining space
    if (isLastColumn) {
      const containerWidth = tableRef.current.clientWidth;
      const totalUsedWidth = visibleColumns.reduce((sum, field) => {
        if (field === columnField) return sum;
        return sum + (columnWidths[field] || 50);
      }, 48); // 48px for checkbox column
      
      const remainingSpace = Math.max(containerWidth - totalUsedWidth, 0);
      // Use the larger of content width or remaining space
      finalWidth = Math.max(finalWidth, remainingSpace);
    }

    handleColumnResize(columnField, finalWidth);
  }, [handleColumnResize]);

  const handleColumnOrderChange = useCallback((newOrder) => {
    setColumnOrder(newOrder);
    updatePreferences(currentPrefs => ({
      ...currentPrefs,
      columnOrder: newOrder
    }));
  }, [updatePreferences]);

  const handleColumnPin = useCallback((columnField) => {
    setPinnedColumns(prev => {
      const isPinning = !prev.includes(columnField);
      const newPinnedColumns = isPinning 
        ? [...prev, columnField]
        : prev.filter(field => field !== columnField);

      // Get current order, ensuring we have a valid array
      const currentOrder = Array.isArray(currentColumnOrder) ? currentColumnOrder : [];
      let newOrder = [...currentOrder];

      if (isPinning) {
        // When pinning, move after last pinned column
        const lastPinnedIndex = currentOrder.reduce((maxIndex, field, index) => {
          return prev.includes(field) ? Math.max(maxIndex, index) : maxIndex;
        }, -1);

        // Remove from current position and insert after last pinned column
        newOrder = [
          ...currentOrder.slice(0, lastPinnedIndex + 1),
          columnField,
          ...currentOrder.filter((field, index) => field !== columnField && index > lastPinnedIndex)
        ];
      } else {
        // When unpinning
        if (newPinnedColumns.length > 0) {
          // If there are still pinned columns, move this column right after them
          const lastPinnedIndex = currentOrder.reduce((maxIndex, field, index) => {
            // Only consider columns that are still pinned (excluding the one being unpinned)
            return newPinnedColumns.includes(field) && field !== columnField ? Math.max(maxIndex, index) : maxIndex;
          }, -1);

          // Remove from current position and insert after last pinned column
          newOrder = [
            ...currentOrder.slice(0, lastPinnedIndex + 1),
            columnField,
            ...currentOrder.filter((field, index) => field !== columnField && index > lastPinnedIndex)
          ];
        }
        // If no pinned columns remain, keep current position (no change to order needed)
      }
      
      // Update both column orders and preferences
      setColumnOrder(newOrder);
      setCurrentColumnOrder(newOrder);
      updatePreferences(currentPrefs => ({
        ...currentPrefs,
        pinnedColumns: newPinnedColumns,
        columnOrder: newOrder
      }));
      
      return newPinnedColumns;
    });
  }, [updatePreferences, currentColumnOrder]);

  // Update currentColumnOrder when columnOrder changes
  useEffect(() => {
    if (JSON.stringify(currentColumnOrder) !== JSON.stringify(columnOrder)) {
      setCurrentColumnOrder(columnOrder);
    }
  }, [columnOrder]);

  const [columns, setColumns] = useState([]);

  const value = useMemo(() => ({
    sortConfig,
    handleSort,
    filterText,
    columns,
    setColumns,
    setFilterText: setFilterTextAndSave,
    filters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterValueChange,
    handleFilterRemove,
    handleFilterClear,
    getDefaultOperator,
    columnSearchText,
    setColumnSearchText,
    isColumnVisible,
    toggleColumnVisibility,
    getVisibleColumns,
    handleDensityChange,
    filterRows,
    sortRows,
    applyFilters,
    loading,
    error,
    preferences: { ...preferences, density, columnOrder: currentColumnOrder, pinnedColumns },
    columnWidths,
    handleColumnResize,
    handleColumnAutoResize,
    handleColumnOrderChange,
    handleColumnReorder,
    handleColumnPin,
    isColumnPinned,
    handleClearSort,
    buyersPremium,
    houseCurrency,
    userCurrency,
    profitMargin,
    rate
  }), [
    sortConfig,
    handleSort,
    filterText,
    setFilterTextAndSave,
    filters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterValueChange,
    handleFilterRemove,
    handleFilterClear,
    getDefaultOperator,
    columnSearchText,
    setColumnSearchText,
    isColumnVisible,
    toggleColumnVisibility,
    getVisibleColumns,
    handleDensityChange,
    filterRows,
    sortRows,
    applyFilters,
    loading,
    error,
    preferences,
    density,
    columnWidths,
    handleColumnResize,
    handleColumnAutoResize,
    handleColumnOrderChange,
    columnOrder,
    pinnedColumns,
    handleClearSort,
    buyersPremium,
    houseCurrency,
    userCurrency,
    profitMargin,
    rate
  ]);

  return (
    <TableContext.Provider value={value}>
      {children}
    </TableContext.Provider>
  );
};
